export interface Result<T> {
  readonly isError: boolean
  readonly isNull: boolean
  readonly isValue: boolean
  readonly fieldName: string

  readonly error: Error
  readonly value: T
  readonly valueNullable: T | null

  map<O>(fn: (value: T, fieldName: string) => O | null): Result<O>
}

function resultGetValue<T>(result: Result<T>): T | null
function resultGetValue<T>(result: Result<T>, defaultValue: T): T
function resultGetValue<T>(result: Result<T>, defaultValue?: T) {
  return result.isValue ? result.value : defaultValue ?? null
}

export const Result = {
  getValue: resultGetValue,
  getError: <T>(result: Result<T>): Error | null => (result.isError ? result.error : null)
}

export class ErrorResult implements Result<any> {
  constructor(public readonly error: Error, public readonly fieldName: string = '') {}

  public readonly isError = true
  public readonly isNull = false
  public readonly isValue = false

  public get value(): any {
    throw new Error('Attempted to access value of an ErrorResult')
  }

  public get valueNullable(): any {
    throw new Error('Attempted to access value of an ErrorResult')
  }

  public map<O>(_fn: (value: any, fieldName: string) => O | null): Result<O> {
    return this
  }
}

export class ValueResult<T> implements Result<T> {
  constructor(private readonly _value: T | null, public readonly fieldName: string = '') {
    this.isNull = _value === null
    this.isValue = !this.isNull
  }

  public readonly isError = false
  public readonly isNull: boolean
  public readonly isValue: boolean

  public get value(): T {
    if (this.isNull) {
      throw new Error('Attempted to access value of a null ValueResult')
    }
    return this._value as T
  }

  public get valueNullable(): T | null {
    return this._value as T | null
  }

  public get error(): Error {
    throw new Error('Attempted to access error of a ValueResult')
  }

  public map<O>(fn: (value: T, fieldName: string) => O | null): Result<O> {
    if (this.isNull) {
      return this as Result<any>
    } else {
      try {
        return new ValueResult(fn(this.value, this.fieldName), this.fieldName)
      } catch (err) {
        return new ErrorResult(err instanceof Error ? err : new Error(), this.fieldName)
      }
    }
  }
}

export const NullResult: Result<any> = new ValueResult(null)

/**
 * Factory function to create front end validators.
 * The returned function should be used as a mapper of a Result.
 *
 * ex:
 * ```typescript
 * const isinValidator = createResultValidatorMapper<string>({
 *    errorMessage: "Only less than 12 chars accepted.",
 *    hasError: res => res.length !== 12
 *  })
 * const isinResult = parseString(...args).map(isinLengthValidator)
 * ```
 */
export const createResultValidatorMapper = <T>({
  hasError,
  errorMessage
}: {
  hasError: (result: T) => boolean
  errorMessage: (result: T) => string
}) => {
  return function validateMapper(result: T): T {
    if (hasError(result)) throw new Error(errorMessage(result))
    return result
  }
}
